/**
 * /tracking/invitation-codes — operator page for feature #172.
 *
 * Declares a TanStack Router route under the Tracking section and renders
 * the `InvitationCodePage` feature. Root wire-up picks this up from
 * `router.tsx` after the prod-main merge.
 *
 * `?inviterUserId=` deep-links the page to a specific inviter so operators
 * can jump here from a user detail view.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { InvitationCodePage } from "@/features/invitation-code/InvitationCodePage";

interface TrackingInvitationCodesSearch {
	inviterUserId?: string;
}

export const trackingInvitationCodesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/invitation-codes",
	validateSearch: (
		raw: Record<string, unknown>,
	): TrackingInvitationCodesSearch => {
		const v = raw.inviterUserId;
		if (typeof v === "string" && v.trim().length > 0) {
			return { inviterUserId: v.trim() };
		}
		return {};
	},
	component: TrackingInvitationCodesRoute,
});

export default trackingInvitationCodesRoute;

export function TrackingInvitationCodesRoute() {
	const { inviterUserId } = trackingInvitationCodesRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-5xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">Invitation codes</li>
				</ol>
			</nav>

			<InvitationCodePage initialInviterUserId={inviterUserId} />
		</div>
	);
}

// Named export for the root wire-up cycle.
export { TrackingInvitationCodesRoute as TrackingInvitationCodesPage };
